import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowLeftRight, Building2, ExternalLink, Receipt } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { financeApi, type OrderPaymentSummary } from '@/services/finance'
import { formatCurrency } from '@/lib/utils'
import { withServiceInstanceId } from '@/lib/service-instance'

const SETTLEMENT_DIRECTION_LABELS: Record<string, string> = {
  none: 'None',
  order_to_job: 'Order company → Job company',
  job_to_order: 'Job company → Order company',
}

const LEDGER_PURPOSE_LABELS: Record<string, string> = {
  commission: 'Commission',
  settlement: 'Settlement',
  prepaid_applied: 'Prepaid applied',
}

type OrderSettlementSummaryPanelProps = {
  orderId: string | number
  orderReferenceId?: string | null
  serviceInstanceId?: string | null
  getJobDetailsPath: (jobId: string | number, jobServiceInstanceId?: string | number | null) => string
}

function SummaryMetric({
  label,
  value,
  hint,
}: {
  label: string
  value: string
  hint?: string
}) {
  return (
    <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
      <p className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className="mt-2 text-lg font-semibold text-foreground">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  )
}

function JobSettlementRow({
  job,
  getJobDetailsPath,
}: {
  job: OrderPaymentSummary['jobs'][number]
  getJobDetailsPath: (jobId: string | number, jobServiceInstanceId?: string | number | null) => string
}) {
  const codPending = Math.max(job.collectable_amount - job.cod_collected_total, 0)

  return (
    <div className="rounded-lg border border-border/70 p-3 space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <Link
            to={getJobDetailsPath(job.job_id)}
            className="inline-flex items-center gap-1 font-medium text-foreground hover:text-primary"
          >
            {formatJobTitleWithId({
              jobId: job.job_id,
              title: job.job_title,
              jobRef: job.job_ref,
            })}
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </div>
        <Badge variant="outline">{job.collection_mode.replace(/_/g, ' ')}</Badge>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 text-sm">
        <div>
          <p className="text-xs text-muted-foreground">Job pay</p>
          <p className="font-medium">{formatCurrency(job.job_pay)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">COD collected</p>
          <p className="font-medium">{formatCurrency(job.cod_collected_total)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">COD pending</p>
          <p className="font-medium">{formatCurrency(codPending)}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">Inter-company due</p>
          <p className="font-medium">{formatCurrency(job.settlement_due)}</p>
        </div>
      </div>

      {job.ledger_entries.length > 0 ? (
        <div className="divide-y divide-border/60 rounded-md border border-border/60 text-sm">
          {job.ledger_entries.map((entry) => (
            <div key={entry.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <span className="inline-flex items-center gap-2 text-muted-foreground">
                <ArrowLeftRight className="h-3.5 w-3.5" />
                {entry.from_instance_id} → {entry.to_instance_id}
                <Badge variant="secondary" className="text-xs">
                  {LEDGER_PURPOSE_LABELS[entry.purpose] ?? entry.purpose}
                </Badge>
              </span>
              <span className="font-medium">{formatCurrency(entry.amount)}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export function OrderSettlementSummaryPanel({
  orderId,
  orderReferenceId,
  serviceInstanceId,
  getJobDetailsPath,
}: OrderSettlementSummaryPanelProps) {
  const summaryQuery = useQuery({
    queryKey: ['order-payment-summary', orderId],
    queryFn: () => financeApi.getOrderPaymentSummary(orderId),
    enabled: Boolean(orderId),
    retry: false,
  })

  const summary = summaryQuery.data
  const settlementPath = withServiceInstanceId('/finance/settlement-invoices', serviceInstanceId)
  const orderPaymentsPath = orderReferenceId
    ? withServiceInstanceId(`/services/orders/${orderReferenceId}#payments`, serviceInstanceId)
    : null

  if (summaryQuery.isLoading) {
    return (
      <Card className="border-border/80">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-56" />
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full rounded-lg" />
          ))}
        </CardContent>
      </Card>
    )
  }

  if (summaryQuery.isError || !summary) {
    return null
  }

  const totals = summary.totals
  const codPending = Math.max(totals.collectable_amount - totals.cod_collected_total, 0)
  const primaryDirection =
    summary.jobs.find((job) => job.settlement_direction && job.settlement_direction !== 'none')
      ?.settlement_direction ?? 'none'

  return (
    <Card className="border-border/80">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
        <div>
          <CardTitle className="text-base">Settlement & job financials</CardTitle>
          <CardDescription>
            Order payments, worker COD, and inter-company settlement across {summary.jobs_count}{' '}
            linked job{summary.jobs_count === 1 ? '' : 's'}.
          </CardDescription>
        </div>
        <Badge variant="outline">{summary.jobs_count} job{summary.jobs_count === 1 ? '' : 's'}</Badge>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryMetric label="Order total" value={formatCurrency(totals.order_total)} />
          <SummaryMetric
            label="Prepaid on order"
            value={formatCurrency(summary.order_payments_total)}
          />
          <SummaryMetric label="Total job pay" value={formatCurrency(totals.job_pay)} />
          <SummaryMetric label="Total commission" value={formatCurrency(totals.commission_amount)} />
          <SummaryMetric label="Worker collectable" value={formatCurrency(totals.collectable_amount)} />
          <SummaryMetric label="COD collected" value={formatCurrency(totals.cod_collected_total)} />
          <SummaryMetric
            label="COD pending"
            value={formatCurrency(codPending)}
            hint={codPending > 0 ? 'Awaiting worker cash collection' : 'Fully collected or prepaid'}
          />
          <SummaryMetric
            label="Inter-company due"
            value={formatCurrency(totals.settlement_due)}
            hint={SETTLEMENT_DIRECTION_LABELS[primaryDirection] ?? primaryDirection}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {orderPaymentsPath ? (
            <Link
              to={orderPaymentsPath}
              className="inline-flex items-center gap-2 rounded-md border border-border/70 bg-background px-3 py-2 text-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
            >
              <Receipt className="h-4 w-4 text-muted-foreground" />
              Order payments
            </Link>
          ) : null}
          <Link
            to={settlementPath}
            className="inline-flex items-center gap-2 rounded-md border border-border/70 bg-background px-3 py-2 text-sm transition-colors hover:border-primary/40 hover:bg-muted/40"
          >
            <Building2 className="h-4 w-4 text-muted-foreground" />
            Settlement invoices
          </Link>
        </div>

        <div className="space-y-3">
          {summary.jobs.map((job) => (
            <JobSettlementRow key={job.job_id} job={job} getJobDetailsPath={getJobDetailsPath} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
